import { defineStore } from 'pinia';
import { DateTime } from 'luxon';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import type { DocumentStatusId } from '@/modules/common/enums/document-status-id.enum.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import DocumentTypeOption from '@/modules/common/models/option/document-type-option.ts';
import ProjectOption from '@/modules/common/models/option/project-option.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { SystemApi } from '@/modules/core/api/system.api.ts';
import { CONTROL_TYPE_AFTER } from '@/modules/document/composables/use-launch-options.ts';
import { createGeneration, EntityEditLock } from '@/modules/core/utils/entity-edit-lock.ts';
import { attemptEditStep, reportEditError } from '@/modules/core/utils/edit-error.util.ts';
import { type EditValidity, isEditValid, updateEditValidity } from '@/modules/core/composables/use-edit-context.ts';
import { useRolesStore } from '@/modules/core/stores/roles.store.ts';
import { Role } from '@/modules/common/enums/role.ts';
import type { ColorCode } from '@/modules/core/enums/color-code.enum.ts';
import { usePreviewStore } from '@/modules/core/stores/preview.store.ts';
import { DocumentApi } from '@/modules/document/api/document.api.ts';
import DocumentLegacyApi from '@/modules/document/api/document-api.ts';
import DocumentMasterApi from '@/modules/document/api/document-master-api.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import { useDocGanttStore } from '@/modules/document/stores/doc-gantt.store.ts';
import { useDocumentRowStore } from '@/modules/document/stores/document-row.store.ts';
import type { Mask } from '@/modules/document/document-master/types/mask.ts';
import type { Parameter } from '@/modules/document/models/parameter.ts';
import {
  carryOverMaskValues,
  createContentSource,
  findSameListOption,
} from '@/modules/document/document-edit/utils/mask-carry-over.ts';
import type { DocumentInternalDto } from '@/modules/document/dto/document-internal.dto.ts';
import type {
  DocumentEditBlock,
  DocumentEditFormData,
  DocumentEditFormState,
  DocumentLaunchMode,
} from '@/modules/document/document-edit/types/document-edit-form.type.ts';
import {
  createDeadline,
  createLaunchDeadline,
  createDelayedLaunch,
  createRegistration,
  type EditSavePlan,
  getLaunchMode,
  isRemovedFromControl,
  parseMaskFormData,
  planSave,
} from '@/modules/document/document-edit/utils/edit-snapshot.ts';
import {
  EMPTY_COLOR_SOURCES,
  findSourceColorLock,
  type SourceColorLock,
} from '@/modules/document/document-edit/utils/color-lock.ts';
import type { DocumentColorSources } from '@/modules/document/types/document-color.type.ts';
import { publishDocumentUpdated } from '@/modules/shared/composables/document-update-sync.ts';

type SessionState = {
  documentId: number | null;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isMaskLoading: boolean;
  validity: EditValidity<DocumentEditBlock>;
  showErrors: boolean;
  maskCache: Record<number, DocumentEditFormState['mask']>;
};

type State = SessionState & {
  snapshot: DocumentInternalDto | null;
  form: DocumentEditFormData | null;
  controllers: Option<number>[];
  systemController: Option<number> | null;
  initialSourceColorLock: SourceColorLock | null;
  initialSourceColor: ColorCode | null;
  colorSources: DocumentColorSources;
  systemUserId: number | null;
  returnControllerId: number | null;
  documentStatus: DocumentStatusId | null;
};

const createSessionState = (): SessionState => ({
  documentId: null,
  isEditing: false,
  isLoading: false,
  isSaving: false,
  isMaskLoading: false,
  validity: {},
  showErrors: false,
  maskCache: {},
});

const createState = (): State => ({
  ...createSessionState(),
  snapshot: null,
  form: null,
  controllers: [],
  systemController: null,
  initialSourceColorLock: null,
  initialSourceColor: null,
  colorSources: EMPTY_COLOR_SOURCES,
  systemUserId: null,
  returnControllerId: null,
  documentStatus: null,
});

const createEmptyMask = (): Mask => ({ parameters: [], formData: {} });

const editLock = new EntityEditLock(TableId.Documents, {
  locked: 'document.edit.notification.locked',
  lockLost: 'document.edit.notification.lockLost',
  lockRenewError: 'document.edit.notification.lockRenewError',
  editingElsewhere: 'document.edit.notification.editingElsewhere',
});

const nextGeneration = createGeneration();

async function loadMask(
  type: Pick<DocumentTypeOption, 'code' | 'mask'>,
  content: string,
  contentMask: string | null
): Promise<Mask> {
  if (!type.mask) {
    return createEmptyMask();
  }

  const [parameters, masks] = await Promise.all([
    DocumentMasterApi.getParameters(type.code),
    contentMask ? DocumentLegacyApi.getMasks([contentMask]) : [],
  ]);

  return { parameters, formData: parseMaskFormData(parameters, content, masks[0]) };
}

async function findListOption(typeId: number, parameter: Pick<Parameter, 'number'>, value: Option) {
  const options = await DocumentMasterApi.getParameterOptions(typeId, parameter.number);

  return findSameListOption(options, value);
}

async function findReturnController(documentId: number, controllers: Option<number>[]) {
  try {
    const name = await DocumentApi.getPreviousControllerName(documentId);
    const matches = controllers.filter(({ label }) => label === name);

    return matches.length === 1 ? matches[0] : null;
  } catch {
    return null;
  }
}

type LoadedSession = Omit<State, keyof SessionState> & {
  snapshot: DocumentInternalDto;
  form: DocumentEditFormData;
  systemUserId: number;
};

function loadRow(documentId: number) {
  const rowStore = useDocumentRowStore();

  return rowStore.item['DOC_ID'] === documentId ? rowStore.item : rowStore.fetchOnly(documentId);
}

async function loadSession(documentId: number): Promise<LoadedSession> {
  const [row, snapshot, types, projects, controllers, color, colorSources, systemUserId] = await Promise.all([
    loadRow(documentId),
    DocumentApi.getInternal(documentId),
    DocumentMasterApi.getTypes(),
    DocumentMasterApi.getProjects(),
    DocumentApi.getAvailableControllers(documentId),
    DocumentApi.getOwnColorState(documentId),
    DocumentApi.getColorSources().catch(() => EMPTY_COLOR_SOURCES),
    SystemApi.getUserId(),
  ]);

  const type =
    types.find(({ code }) => code === snapshot.DT_ID) ??
    DocumentTypeOption.createByResponse({
      DT_ID: snapshot.DT_ID,
      Название: row['Тип'],
      Маска: row['Содержание_MASK'],
      Активен: 1,
    });
  const project =
    projects.find(({ code }) => code === snapshot.P_ID) ??
    new ProjectOption({ P_ID: snapshot.P_ID, Название: row['Проект'], Активен: 1 });
  const allControllerOptions = controllers.map(({ LOGIN_ID, ФИО }) => ({ code: LOGIN_ID, label: ФИО }));
  const systemController = allControllerOptions.find(({ code }) => code === systemUserId) ?? null;
  const regularControllerOptions = allControllerOptions.filter(({ code }) => code !== systemUserId);
  const removedFromControl = isRemovedFromControl(snapshot, systemUserId);
  const [returnController, mask] = await Promise.all([
    removedFromControl ? findReturnController(documentId, regularControllerOptions) : null,
    loadMask(type, snapshot.Содержание, (row['Содержание_MASK'] as string | null) ?? null),
  ]);

  const controller = allControllerOptions.find(({ code }) => code === snapshot.CONTROLLER_ID) ?? {
    code: snapshot.CONTROLLER_ID,
    label: String(row['Контролер'] ?? ''),
  };

  return {
    snapshot,
    controllers: regularControllerOptions,
    systemController,
    initialSourceColorLock: color.isLockedBySource ? { color: color.sourceColor, source: color.lockSource } : null,
    initialSourceColor: color.sourceColor,
    colorSources,
    systemUserId,
    returnControllerId: returnController?.code ?? null,
    documentStatus: (row['DS_ID'] as DocumentStatusId | undefined) ?? null,
    form: {
      type,
      project,
      mask,
      content: snapshot.Содержание,
      registration: createRegistration(snapshot),
      controller,
      deadline: createDeadline(snapshot.Срок),
      removedFromControl,
      launchControlType: CONTROL_TYPE_AFTER,
      launch: createLaunchDeadline(snapshot.Запуск),
      ...createDelayedLaunch(snapshot, systemUserId),
      color: color.color,
      colorLocked: color.isLocked,
      comment: snapshot.Комментарий ?? '',
    },
  };
}

async function applySavePlan(documentId: number, plan: EditSavePlan, systemUserId: number | null) {
  const { registration, removeFromControl, launchDate, pause } = plan;
  const results: boolean[] = [];

  if (registration) {
    results.push(
      await attemptEditStep('document.edit.notification.registrationError', () =>
        DocumentApi.setRegistrationData(documentId, registration.number, registration.date)
      )
    );
  }

  if (launchDate) {
    results.push(
      await attemptEditStep('document.edit.notification.launchError', () =>
        DocumentApi.setStartDate(documentId, launchDate)
      )
    );
  }

  if (pause) {
    results.push(await attemptEditStep('document.edit.notification.launchError', () => DocumentApi.pause(documentId)));
  }

  if (removeFromControl && systemUserId) {
    results.push(
      await attemptEditStep('document.edit.notification.controlError', () =>
        DocumentApi.setController(documentId, systemUserId)
      )
    );
  }

  return results.every(Boolean);
}

export const useDocumentEditStore = defineStore(StoreId.DocumentEdit, {
  state: createState,
  getters: {
    isValid: (state) => isEditValid(state.validity),
    isEditingDocument: (state) => (documentId: number) => state.isEditing && state.documentId === documentId,
    launchMode: (state): DocumentLaunchMode => getLaunchMode(state.documentStatus),
    controllerOptions: ({ controllers, form, systemController, systemUserId }) =>
      form?.controller?.code === systemUserId && systemController ? [...controllers, systemController] : controllers,
    warnsAboutEditLoss: () => !useRolesStore().has(Role.Administrator),
    displayedColor(): ColorCode | null {
      const { form, snapshot, initialSourceColor, colorSources, sourceColorLock } = this;
      if (!form || !snapshot) {
        return null;
      }

      if (sourceColorLock) {
        return sourceColorLock.color;
      }

      if (form.color !== null || form.colorLocked) {
        return form.color;
      }

      const projectId = form.project?.code ?? null;
      if (form.type.code === snapshot.DT_ID && projectId === snapshot.P_ID) {
        return initialSourceColor;
      }

      return (
        colorSources.types[form.type.code]?.color ??
        (projectId !== null ? colorSources.projects[projectId]?.color : null) ??
        null
      );
    },
    sourceColorLock: ({ form, snapshot, initialSourceColorLock, colorSources }): SourceColorLock | null => {
      if (!form || !snapshot) {
        return null;
      }

      const projectId = form.project?.code ?? null;

      if (form.type.code === snapshot.DT_ID && projectId === snapshot.P_ID) {
        return initialSourceColorLock;
      }

      return findSourceColorLock(colorSources, form.type.code, projectId);
    },
  },
  actions: {
    async open(documentId: number) {
      if (this.isEditing || this.isLoading) {
        return;
      }

      const isCurrent = nextGeneration();

      this.isLoading = true;

      try {
        const session = await editLock.acquire(
          documentId,
          isCurrent,
          () => loadSession(documentId),
          () => void this.close({ releaseLock: false })
        );

        if (session) {
          this.$patch((state) => {
            Object.assign(state, { ...session.data, documentId, isEditing: true });
          });
        }
      } catch (error) {
        if (isCurrent()) {
          this.$reset();
          reportEditError('document.edit.notification.openError', error);
        }
      } finally {
        if (isCurrent()) {
          this.isLoading = false;
        }
      }
    },

    patchForm(patch: Partial<DocumentEditFormState>) {
      if (this.isEditing && this.form) {
        Object.assign(this.form, patch);
      }
    },

    setDelayedLaunch(delayedLaunch: boolean) {
      if (!delayedLaunch && this.form?.stopControlBeforeLaunch) {
        this.setStopControlBeforeLaunch(false);
      }

      this.patchForm({ delayedLaunch });
    },

    setStopControlBeforeLaunch(stopControlBeforeLaunch: boolean) {
      if (!this.form) {
        return;
      }

      if (stopControlBeforeLaunch) {
        if (this.form.controller?.code !== this.systemUserId) {
          this.returnControllerId = this.form.controller?.code ?? this.returnControllerId;
        }

        this.patchForm({
          stopControlBeforeLaunch,
          controller: this.systemController ?? this.form.controller,
        });
        return;
      }

      const returnController = this.controllers.find(({ code }) => code === this.returnControllerId);

      this.patchForm({
        stopControlBeforeLaunch,
        controller:
          this.form.controller?.code === this.systemUserId ? (returnController ?? null) : this.form.controller,
      });
    },

    async setType(type: DocumentEditFormState['type']) {
      if (!this.isEditing || !this.form || !this.snapshot || this.form.type.code === type.code) {
        return;
      }

      const isCurrent = nextGeneration();
      const previous = this.form.mask;

      if (previous.parameters.length) {
        this.maskCache = { ...this.maskCache, [this.form.type.code]: previous };
      }

      this.patchForm({
        type,
        mask: createEmptyMask(),
        registration: this.form.registration.auto ? createRegistration(this.snapshot) : this.form.registration,
      });
      this.isMaskLoading = true;

      try {
        const { parameters } = await loadMask(type, '', null);
        const cached = this.maskCache[type.code];
        const sources = [
          previous.parameters.length ? previous : createContentSource(this.form.content),
          ...Object.values(this.maskCache).filter((mask) => mask !== previous),
        ];
        const formData = await carryOverMaskValues(parameters, cached?.formData ?? {}, sources, (parameter, value) =>
          findListOption(type.code, parameter, value)
        );

        if (isCurrent()) {
          this.patchForm({ mask: { parameters, formData } });
        }
      } catch (error) {
        if (isCurrent()) {
          reportEditError('document.edit.notification.maskError', error);
        }
      } finally {
        if (isCurrent()) {
          this.isMaskLoading = false;
        }
      }
    },

    setAutoRegistration(auto: boolean) {
      if (!this.form || !this.snapshot) {
        return;
      }

      this.patchForm({
        registration: auto
          ? { number: this.form.type.template ?? '', date: DateTime.now(), auto }
          : createRegistration(this.snapshot),
      });
    },

    setValidity(block: DocumentEditBlock, isValid: boolean | null) {
      this.validity = updateEditValidity(this.validity, block, isValid);
    },

    async save() {
      if (!this.isEditing || !this.form || !this.snapshot || this.isSaving) {
        return false;
      }

      if (this.isMaskLoading || !this.isValid) {
        this.showErrors = true;
        return false;
      }

      const { snapshot, systemUserId, returnControllerId, launchMode } = this;
      const form = this.sourceColorLock ? { ...this.form, color: null, colorLocked: false } : this.form;
      const documentId = snapshot.DOC_ID;
      let plan: EditSavePlan;

      this.isSaving = true;

      try {
        if (!(await editLock.isFree(documentId))) {
          this.isSaving = false;
          return false;
        }

        plan = planSave(form, snapshot, systemUserId, returnControllerId, launchMode);
        await DocumentApi.edit(plan.params);
      } catch (error) {
        reportEditError('document.edit.notification.saveError', error);
        this.isSaving = false;
        return false;
      }

      const isApplied = await applySavePlan(documentId, plan, systemUserId);

      if (isApplied) {
        notification.success('document.edit.notification.saved');
      } else {
        notification.warning('document.edit.notification.savedPartially');
      }

      await this.close();
      void usePreviewStore().refreshPartlyPreviewAction(DocumentEndPoint.GetList, documentId);
      void useDocGanttStore().fetch({ doc_id: documentId });
      publishDocumentUpdated(documentId);

      return true;
    },

    async close({ releaseLock: shouldReleaseLock = true }: { releaseLock?: boolean } = {}) {
      const { documentId } = this;

      nextGeneration();
      // Данные формы не сбрасываем: на них DocumentEditForm доигрывает leave-переход, open() их перезапишет.
      this.$patch((state) => {
        Object.assign(state, createSessionState());
      });

      await editLock.close(documentId, shouldReleaseLock);
    },
  },
});
